/**
 * Quote calculator — composes the library, draft, and AI-training hooks
 * with the math that turns user inputs into a quote total.
 *
 * Refactored 2026-05-19: previously a 332-line monolith; library/draft/
 * training concerns now live in sibling hooks (`useQuoteLibrary`,
 * `useQuoteDraft`, `useTrainingProfile`). The public return shape is
 * unchanged so `QuoteGenerator.jsx` and the existing Jest suite require
 * no edits.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { computeQuoteConfidence } from '../../lib/quoteConfidence';
import { suggestLaborRate } from './pricingIntelligence';
import { useQuoteLibrary } from './useQuoteLibrary';
import { useTrainingProfile } from './useTrainingProfile';
import { draftValue, clearDraft, useQuoteDraftPersistence } from './useQuoteDraft';

// Picks the right user-input field for a given measurement type. Pure so
// it can be reused for both the calculation and the confidence score.
const pickMeasurement = (type, { sf, lf, hr, ct }) => {
  switch (type) {
    case 'linear_feet': return parseFloat(lf) || 0;
    case 'hourly':      return parseFloat(hr) || 0;
    case 'count':       return parseFloat(ct) || 0;
    case 'square_feet':
    default:            return parseFloat(sf) || 0;
  }
};

const measurementLabelFor = (type, value) => {
  switch (type) {
    case 'linear_feet': return `${value} linear ft`;
    case 'hourly':      return `${value} hours`;
    case 'count':       return `${value} units`;
    default:            return `${value} sq ft`;
  }
};

export const useQuoteCalculation = () => {
  const { projectTypes, setProjectTypes, addOns, setAddOns, refreshLibrary } = useQuoteLibrary();
  const training = useTrainingProfile();

  // Hydrate inputs from the saved draft (falls back to sensible defaults).
  const initialPT = projectTypes[0]?.value || projectTypes[0]?.id || 'interior-paint';
  const [projectType, setProjectType] = useState(() => draftValue('projectType', initialPT));
  const [squareFeet, setSquareFeet] = useState(() => draftValue('squareFeet', '500'));
  const [linearFeet, setLinearFeet] = useState(() => draftValue('linearFeet', '20'));
  const [hours, setHours] = useState(() => draftValue('hours', '4'));
  const [count, setCount] = useState(() => draftValue('count', '5'));
  const [laborRate, setLaborRate] = useState(() => draftValue('laborRate', '50'));
  const [markup, setMarkup] = useState(() => draftValue('markup', '20'));
  const [customMaterialRate, setCustomMaterialRate] = useState(() => draftValue('customMaterialRate', ''));
  const [selectedAddOns, setSelectedAddOns] = useState(() => draftValue('selectedAddOns', []));
  const [customerName, setCustomerName] = useState(() => draftValue('customerName', ''));
  const [quote, setQuote] = useState(null);

  // If the saved draft's projectType no longer exists in the library (e.g. the
  // user deleted a custom trade), drop back to the first available one.
  useEffect(() => {
    if (projectTypes.length && !projectTypes.some((p) => (p.value || p.id) === projectType)) {
      setProjectType(projectTypes[0].value || projectTypes[0].id);
    }
  }, [projectTypes, projectType]);

  // Per-trade smart labor-rate defaults: when the user picks a new trade we
  // set a typical 2026 hourly rate for it — UNLESS they've manually typed
  // their own rate this session (tracked via `laborRateUserSet` so explicit
  // overrides aren't stomped).
  const [laborRateUserSet, setLaborRateUserSet] = useState(false);
  const setLaborRateManual = useCallback((value) => {
    setLaborRateUserSet(true);
    setLaborRate(value);
  }, []);
  useEffect(() => {
    if (laborRateUserSet) return;
    setLaborRate(String(suggestLaborRate(projectType)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectType]);

  // Persist every relevant input change to localStorage.
  useQuoteDraftPersistence({
    projectType, squareFeet, linearFeet, hours, count,
    laborRate, markup, customMaterialRate, selectedAddOns, customerName,
  });

  const debouncedSquareFeet = useDebounce(squareFeet, 200);
  const debouncedLinearFeet = useDebounce(linearFeet, 200);
  const debouncedHours = useDebounce(hours, 200);
  const debouncedCount = useDebounce(count, 200);
  const debouncedLaborRate = useDebounce(laborRate, 200);
  const debouncedMarkup = useDebounce(markup, 200);
  const debouncedCustomMaterialRate = useDebounce(customMaterialRate, 200);

  const getProjectTypeData = useCallback(
    () => projectTypes.find((pt) => (pt.value || pt.id) === projectType) || projectTypes[0],
    [projectTypes, projectType],
  );

  const calculateQuote = useCallback(() => {
    const projectData = getProjectTypeData();
    if (!projectData) return;

    const measurement = pickMeasurement(projectData.measurementType, {
      sf: debouncedSquareFeet, lf: debouncedLinearFeet,
      hr: debouncedHours,      ct: debouncedCount,
    });
    const measurementLabel = measurementLabelFor(projectData.measurementType, measurement);

    const rate = parseFloat(debouncedLaborRate) || 0;
    const markupPercent = parseFloat(debouncedMarkup) || 0;

    const materialRatePerUnit = debouncedCustomMaterialRate
      ? parseFloat(debouncedCustomMaterialRate)
      : (projectData.materialRate || 0);
    const materialCost = measurement * materialRatePerUnit;

    const estimatedHours = measurement * (projectData.laborHours || 0);
    const laborCost = estimatedHours * rate;

    const addOnsCost = selectedAddOns.reduce((total, addonId) => {
      const addon = addOns.find((a) => a.id === addonId);
      return total + (addon ? Number(addon.price) || 0 : 0);
    }, 0);

    const subtotal = materialCost + laborCost + addOnsCost;
    const markupAmount = (subtotal * markupPercent) / 100;
    const total = subtotal + markupAmount;

    setQuote({
      projectName: projectData.label,
      measurement: measurementLabel,
      squareFeet: projectData.measurementType === 'square_feet' ? measurement : null,
      linearFeet: projectData.measurementType === 'linear_feet' ? measurement : null,
      hours:      projectData.measurementType === 'hourly'      ? measurement : null,
      count:      projectData.measurementType === 'count'       ? measurement : null,
      materialCost: materialCost.toFixed(2),
      laborCost:    laborCost.toFixed(2),
      laborHours:   estimatedHours.toFixed(1),
      addOnsCost:   addOnsCost.toFixed(2),
      subtotal:     subtotal.toFixed(2),
      markup:       markupAmount.toFixed(2),
      markupPercent,
      total:        total.toFixed(2),
    });
  }, [
    getProjectTypeData, debouncedSquareFeet, debouncedLinearFeet, debouncedHours,
    debouncedCount, debouncedLaborRate, debouncedMarkup, debouncedCustomMaterialRate,
    selectedAddOns, addOns,
  ]);

  useEffect(() => { calculateQuote(); }, [calculateQuote, projectType, projectTypes]);

  const toggleAddOn = useCallback((addonId) => {
    setSelectedAddOns((prev) =>
      prev.includes(addonId) ? prev.filter((id) => id !== addonId) : [...prev, addonId],
    );
  }, []);

  // Live Quote Confidence Score — recomputes whenever the quote or the
  // user's measurement input changes.
  const confidence = useMemo(() => {
    const pt = getProjectTypeData();
    if (!pt) return null;
    const measureValue = pickMeasurement(pt.measurementType, {
      sf: debouncedSquareFeet, lf: debouncedLinearFeet,
      hr: debouncedHours,      ct: debouncedCount,
    });
    return computeQuoteConfidence({
      quote,
      training,
      projectTypeSlug: projectType,
      measurement: measureValue,
    });
  }, [
    getProjectTypeData, quote, training, projectType,
    debouncedSquareFeet, debouncedLinearFeet, debouncedHours, debouncedCount,
  ]);

  return {
    // State
    projectType,
    setProjectType,
    squareFeet,
    setSquareFeet,
    linearFeet,
    setLinearFeet,
    hours,
    setHours,
    count,
    setCount,
    laborRate,
    setLaborRate: setLaborRateManual,
    markup,
    setMarkup,
    customMaterialRate,
    setCustomMaterialRate,
    selectedAddOns,
    toggleAddOn,
    customerName,
    setCustomerName,
    quote,

    // Data
    projectTypes,
    setProjectTypes,
    addOns,
    setAddOns,
    getProjectTypeData,
    confidence,

    // Actions
    calculateQuote,
    clearDraft,
    refreshLibrary,
  };
};
